/**
 * The sidebar picker's data: server-side search, paged, never the whole list.
 *
 * - Opening asks for the first page (40 rows) and the Recent rows, in parallel.
 * - Typing is debounced; each settled query asks the SERVER, which searches
 *   the whole workspace — a project on page nine is one request away, not nine.
 * - `loadMore()` appends the next page; the list calls it as the reader
 *   scrolls near the end. A request already in flight is never duplicated.
 * - Every response is tagged with the query generation it answers. A slow
 *   answer to an old query is dropped rather than painted over a newer one.
 * - The first unfiltered page is cached per workspace for a minute, so
 *   reopening the picker paints immediately and refreshes behind the paint.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { fetchPickerProjectsById, fetchProjectPickerPage } from "../api";
import type { PickerLetter, PickerProject, ProjectPickerPage } from "../../server/queries/projectPicker";

export const PICKER_DEBOUNCE_MS = 200;
const FIRST_PAGE_TTL_MS = 60_000;
/** How many recent projects the Recent section shows. */
export const RECENT_LIMIT = 4;

const firstPageCache = new Map<string, { page: ProjectPickerPage; at: number }>();

/** Test seam. */
export function clearProjectPickerCache(): void {
  firstPageCache.clear();
}

export function workspaceKeyOf(search: string): string {
  return new URLSearchParams(search).get("workspace") ?? "default";
}

// ── Recent ────────────────────────────────────────────────────────────────
// Remembered in the browser, per workspace: the console owns no writes, and
// "the last few projects you opened here" is a per-viewer convenience. Every
// access is guarded — storage can be absent or throw (private windows,
// blocked site data), and the picker must work without it.

function recentKey(workspaceKey: string): string {
  return `ledai.recentProjects.${workspaceKey}`;
}

export function readRecentProjectIds(workspaceKey: string): string[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(recentKey(workspaceKey)) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string").slice(0, RECENT_LIMIT) : [];
  } catch {
    return [];
  }
}

export function rememberRecentProject(workspaceKey: string, id: string): void {
  try {
    const next = [id, ...readRecentProjectIds(workspaceKey).filter((existing) => existing !== id)].slice(0, RECENT_LIMIT);
    localStorage.setItem(recentKey(workspaceKey), JSON.stringify(next));
  } catch {
    // A picker without memory is still a picker.
  }
}

// ── The hook ──────────────────────────────────────────────────────────────

export interface ProjectPickerState {
  /** What the input shows, keystroke by keystroke. */
  query: string;
  /** The query the rows answer — `query` once the debounce settles. */
  settledQuery: string;
  rows: PickerProject[];
  recent: PickerProject[];
  letters: PickerLetter[];
  total: number;
  matchCount: number;
  hasMore: boolean;
  status: "loading" | "ready" | "error";
  loadingMore: boolean;
  /** A next page failed; the rows already shown stay. */
  moreFailed: boolean;
}

const INITIAL: ProjectPickerState = {
  query: "",
  settledQuery: "",
  rows: [],
  recent: [],
  letters: [],
  total: 0,
  matchCount: 0,
  hasMore: false,
  status: "loading",
  loadingMore: false,
  moreFailed: false,
};

export interface ProjectPickerDeps {
  fetchPage?: typeof fetchProjectPickerPage;
  fetchByIds?: typeof fetchPickerProjectsById;
  debounceMs?: number;
}

export function useProjectPicker(search: string, open: boolean, deps: ProjectPickerDeps = {}) {
  const fetchPage = deps.fetchPage ?? fetchProjectPickerPage;
  const fetchByIds = deps.fetchByIds ?? fetchPickerProjectsById;
  const debounceMs = deps.debounceMs ?? PICKER_DEBOUNCE_MS;
  const workspaceKey = workspaceKeyOf(search);

  const [state, setState] = useState<ProjectPickerState>(INITIAL);
  const generation = useRef(0);
  const nextOffset = useRef<number | null>(null);
  const inFlightMore = useRef(false);

  /** Replace the rows with the first page for `query`. */
  const loadFirst = useCallback(
    (query: string) => {
      const mine = ++generation.current;
      inFlightMore.current = false;
      const cached = !query ? firstPageCache.get(workspaceKey) : undefined;
      const apply = (page: ProjectPickerPage) => {
        nextOffset.current = page.nextOffset;
        setState((s) => ({
          ...s,
          settledQuery: query,
          rows: page.items,
          letters: page.letters ?? (query ? [] : s.letters),
          total: page.total,
          matchCount: page.matchCount,
          hasMore: page.nextOffset !== null,
          status: "ready",
          loadingMore: false,
          moreFailed: false,
        }));
      };
      if (cached) apply(cached.page);
      else setState((s) => ({ ...s, settledQuery: query, status: "loading", loadingMore: false, moreFailed: false }));
      if (cached && Date.now() - cached.at < FIRST_PAGE_TTL_MS) return;

      fetchPage(search, { query, offset: 0 }).then(
        (page) => {
          if (generation.current !== mine) return;
          if (!query) firstPageCache.set(workspaceKey, { page, at: Date.now() });
          apply(page);
        },
        () => {
          if (generation.current !== mine) return;
          // A cached page on screen stays on screen; only an empty list errors.
          if (!cached) setState((s) => ({ ...s, status: "error" }));
        },
      );
    },
    [fetchPage, search, workspaceKey],
  );

  // Open: first page + Recent. Close: forget the query.
  useEffect(() => {
    if (!open) {
      generation.current += 1;
      setState(INITIAL);
      return;
    }
    loadFirst("");
    const ids = readRecentProjectIds(workspaceKey);
    if (ids.length === 0) return;
    let live = true;
    fetchByIds(search, ids).then(
      (response) => live && setState((s) => ({ ...s, recent: response.items })),
      () => undefined,
    );
    return () => {
      live = false;
    };
    // Keyed on opening and on the workspace only: `loadFirst` also changes
    // with `?site=`, and picking a project must not refetch the open list.
  }, [open, workspaceKey]);

  // Debounce the query into a server search.
  useEffect(() => {
    if (!open || state.query === state.settledQuery) return;
    const handle = setTimeout(() => loadFirst(state.query.trim()), debounceMs);
    return () => clearTimeout(handle);
  }, [open, state.query, state.settledQuery, debounceMs, loadFirst]);

  const setQuery = useCallback((query: string) => setState((s) => ({ ...s, query })), []);

  const loadMore = useCallback(() => {
    const offset = nextOffset.current;
    if (offset === null || inFlightMore.current) return;
    inFlightMore.current = true;
    const mine = generation.current;
    const query = state.settledQuery;
    setState((s) => ({ ...s, loadingMore: true, moreFailed: false }));
    fetchPage(search, { query, offset }).then(
      (page) => {
        if (generation.current !== mine) return;
        inFlightMore.current = false;
        nextOffset.current = page.nextOffset;
        setState((s) => {
          // Never append a row twice, even if the list shifted between pages.
          const seen = new Set(s.rows.map((row) => row.id));
          return {
            ...s,
            rows: [...s.rows, ...page.items.filter((row) => !seen.has(row.id))],
            total: page.total,
            matchCount: page.matchCount,
            hasMore: page.nextOffset !== null,
            loadingMore: false,
          };
        });
      },
      () => {
        if (generation.current !== mine) return;
        inFlightMore.current = false;
        setState((s) => ({ ...s, loadingMore: false, moreFailed: true }));
      },
    );
  }, [fetchPage, search, state.settledQuery]);

  const retry = useCallback(() => loadFirst(state.settledQuery), [loadFirst, state.settledQuery]);

  return { state, setQuery, loadMore, retry };
}
